import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect } from 'react';
import {
    Box,
    css,
    DropzoneArea,
    DropzoneFileList,
    FileRejection,
    Icon,
    styled,
    Text,
    useDropzone,
    useToast,
    VStack,
} from '@cad-arc';
import { LoadingIndicator } from '../common/components/loading-indicator';
import { errorOccurredWhile } from '../common/helpers/error-occurred-while';
import { AttachmentsThumbnailGrid } from './attachments-thumbnail-grid';
import { MergedAttachmentsSection } from './merged-attachments-section';
import { useAttachments } from './hooks/use-attachments';
import { useUploadAttachment } from './hooks/use-upload-attachment';
import { DEFAULT_ATTACHMENT_SORT_OPTION_VALUE, MAX_UPLOAD_SIZE } from './constants';
import { useAttachmentsUploadStatus } from './hooks/use-attachments-upload-status';
import { useAttachmentsUploadStore } from './attachments-upload-store-provider';
import { AttachmentUploadDeniedError } from './errors';
import { AttachmentSortOptionValue, AttachmentsPanelType } from './types';
import { useAttachmentsAbilities } from './hooks/use-attachments-abilities';
import { useUploadValidation } from './hooks/use-upload-validation';
import { getFileRejectionErrorMessage } from './helpers/get-file-rejection-error-message';
import { getFileServerId } from './helpers';
import { useBootstrapState } from '~/bootstrap/bootstrap-provider';

export interface AttachmentsViewProps {
    entityId: number;
    entityType: AttachmentsPanelType;
    entityNumber?: string;
    onOpenPreview?: (attachmentId: number) => void;
    onAttachmentCountChange?: (attachmentsConut: number, mergedAttachmentCount: number) => void;
    sortOption?: AttachmentSortOptionValue;
    isReadOnly?: boolean;
}

export interface AttachmentsViewHandle {
    openFileDialog: () => void;
}

const StyledDropzone = styled(DropzoneArea)`
    /* Apply flex-grow to the dropzone root elementto ensure it takes up available space */
    & {
        flex-grow: 1;

        /* margin/padding space dashed border from thumbnails while keeping first column aligned with title*/
        margin: -4px;
        margin-top: 0px;
        padding: 4px;
        padding-top: 4px;
    }

    /* Center uploader and fill up space */
    & .arc-dropzone-uploader {
        flex: 1;
        justify-content: center;
    }

    /* Prevent content area from creating gap when uploader is visible */
    &:not(:has(.arc-dropzone-uploader)) .arc-dropzone-area__content {
        flex: 1;
    }

    /* show dashed border when dropping attachment to dropzone area */
    ${(props) =>
        props.isDragActive &&
        css`
            &:not(:has(.arc-dropzone-uploader)) {
                outline: 1px dashed var(--arc-colors-control-border-default);
                outline-offset: -1px;
                background-color: var(--arc-colors-interactive-hover);

                /* override area__content background so padded area between dash and thumbnails has hover treatment */
                & .arc-dropzone-area__content {
                    background-color: transparent !important;
                }
            }
        `}
`;

export const AttachmentsView = observer(
    React.forwardRef<AttachmentsViewHandle, AttachmentsViewProps>(function AttachmentsView(
        {
            entityId,
            entityType,
            entityNumber,
            onOpenPreview,
            onAttachmentCountChange,
            sortOption = DEFAULT_ATTACHMENT_SORT_OPTION_VALUE,
            isReadOnly = false,
        },
        ref
    ): React.ReactElement {
        const {
            data: attachmentsData,
            error,
            isLoading,
        } = useAttachments({ entityId, entityType });
        const attachments = attachmentsData?.attachments;
        const mergedAttachments = attachmentsData?.mergedAttachments;
        const toast = useToast();

        const handleUploadError = useCallback(
            function handleUploadError({ error, file }: { error: Error; file: File }) {
                if (error instanceof AttachmentUploadDeniedError) {
                    toast({
                        status: 'error',
                        description: `"${file.name}" is not a supported file type.`,
                    });
                } else {
                    toast({
                        status: 'error',
                        description: `"${file.name}" failed to upload.`,
                    });
                }
            },
            [toast]
        );

        const uploadMutation = useUploadAttachment({
            entityId,
            entityType,
            entityNumber,
            onUploadError: handleUploadError,
        });
        const { canAddAttachments } = useAttachmentsAbilities(entityType);
        const { uploads, hasActiveUploads, getUploadProgress, removeFailedUpload } =
            useAttachmentsUploadStatus(entityId, entityType);
        const uploadStore = useAttachmentsUploadStore();
        const bootstrapState = useBootstrapState();
        const maxConcurrentUploads = bootstrapState.maxConcurrentUploadsPerUser ?? 10;

        const { validateConcurrentUploads, validateMaxAttachments, maxAttachments } =
            useUploadValidation({
                entityType,
                attachments,
                uploads,
                maxConcurrentUploads,
            });

        const cleanupFailedUploads = useCallback(
            function removeFailedUploadsFromGallery() {
                const failedUploads = uploads.filter((upload) => upload.status === 'failed');
                failedUploads.forEach(removeFailedUpload);
            },
            [uploads, removeFailedUpload]
        );

        const uploadAttachment = useCallback(
            function triggerFileUpload(file: File) {
                cleanupFailedUploads();
                uploadMutation.mutate(file);
            },
            [uploadMutation, cleanupFailedUploads]
        );

        const attachmentCount = attachments?.length || 0;
        const mergedAttachmentCount = mergedAttachments?.length || 0;
        const hasAttachments = attachmentCount > 0 || mergedAttachmentCount > 0;
        const isMaxReached = attachmentCount >= maxAttachments;
        const allowDragAndDropUpload =
            canAddAttachments && !hasAttachments && !hasActiveUploads && !isReadOnly;
        const showReadOnlyEmptyState = !canAddAttachments && !hasAttachments && !hasActiveUploads;
        // pass all pending and error'd uploads to show in the gallery
        const pendingOrFailedUploads = uploads.filter((upload) => upload.status !== 'completed');
        const onDrop = useCallback(
            function handleFileDrop(acceptedFiles: File[], fileRejections: FileRejection[]) {
                // Validation 1: Single file check
                if (acceptedFiles.length + fileRejections.length > 1) {
                    toast({
                        status: 'error',
                        description: 'Too many files selected. Please upload one file at a time.',
                    });
                    return;
                }

                // Validation 2: Concurrent uploads check
                const concurrentValidation = validateConcurrentUploads();
                if (!concurrentValidation.isValid) {
                    toast({ status: 'error', description: concurrentValidation.error });
                    return;
                }

                // Validation 3: Max attachments check
                const maxAttachmentsValidation = validateMaxAttachments();
                if (!maxAttachmentsValidation.isValid) {
                    toast({ status: 'error', description: maxAttachmentsValidation.error });
                    return;
                }

                // Handle accepted files
                if (acceptedFiles.length > 0) {
                    uploadAttachment(acceptedFiles[0]);
                    return;
                }

                // Handle rejected files
                if (fileRejections.length > 0) {
                    const errorMessage = getFileRejectionErrorMessage(fileRejections[0]);
                    toast({ status: 'error', description: errorMessage });
                }
            },
            [validateConcurrentUploads, validateMaxAttachments, uploadAttachment, toast]
        );
        const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
            onDrop,
            maxFiles: maxAttachments,
            maxSize: MAX_UPLOAD_SIZE,
            noClick: true,
            isDisabled: isMaxReached || isReadOnly,
        });

        React.useImperativeHandle(
            ref,
            () => ({
                openFileDialog: () => open(),
            }),
            [open]
        );

        useEffect(() => {
            onAttachmentCountChange?.(attachmentCount, mergedAttachmentCount);
        }, [attachmentCount, mergedAttachmentCount, onAttachmentCountChange]);

        useEffect(
            function reconcileProcessingUploadsWithFetchedAttachments() {
                if (!attachmentsData?.attachments) return;
                attachmentsData.attachments.forEach((attachment) => {
                    const fileServerId = getFileServerId(attachment);
                    if (fileServerId) {
                        uploadStore.handleUploadCompletionByFileServerFileId({
                            fileServerFileId: fileServerId,
                            success: true,
                        });
                    }
                });
            },
            [attachmentsData, uploadStore]
        );

        if (error) {
            return <Box>{errorOccurredWhile('loading attachments')}</Box>;
        }

        if (isLoading) {
            return <LoadingIndicator />;
        }

        return (
            <Box height="100%" minH={0} display="flex" flexDirection="column">
                {showReadOnlyEmptyState ? (
                    <Box pt={3} display="flex" justifyContent="center">
                        <VStack>
                            <Icon icon="Paperclip" size="md" color="text.tertiary" />
                            <Text fontSize="sm" fontWeight="bold" lineHeight="16px">
                                No attachments
                            </Text>
                        </VStack>
                    </Box>
                ) : (
                    <StyledDropzone
                        {...getRootProps()}
                        isDisabled={!canAddAttachments || isMaxReached || isReadOnly}
                        helpText="Drop a single file up to 3 GB"
                        inputProps={getInputProps()}
                        onClick={open}
                        isDragActive={isDragActive}
                        showUploader={allowDragAndDropUpload}
                    >
                        <DropzoneFileList variant="thumbnail">
                            <AttachmentsThumbnailGrid
                                attachments={attachments || []}
                                entityId={entityId}
                                entityType={entityType}
                                entityNumber={entityNumber}
                                onOpenPreview={onOpenPreview}
                                variant="grid"
                                uploads={pendingOrFailedUploads}
                                getUploadProgress={getUploadProgress}
                                sortOption={sortOption}
                                onClickRemoveFailedUpload={removeFailedUpload}
                                isReadOnly={isReadOnly}
                            />
                            <MergedAttachmentsSection
                                parentEventId={entityId}
                                mergedAttachments={mergedAttachments || []}
                                entityType={entityType}
                                onOpenPreview={onOpenPreview}
                                sortOption={sortOption}
                            />
                        </DropzoneFileList>
                    </StyledDropzone>
                )}
            </Box>
        );
    })
);
